import React, { ChangeEvent } from "react";
import styled from "styled-components";

interface ProgressBarProps {
    value: number;
    width?: string;
    height?: string;
    backgroundColor?: string;
    progressColor?: string;
    borderRadius?: string;
    label?: string;
    description?: string;
    showTooltip?: boolean;
    showSteps?: boolean;
    onChange?: (value: number) => void;
    max?: number;
    min?: number;
    step?: number;
    className?: string;
    readOnly?: boolean;
    scale?: string[];
}

const ProgressBar: React.FC<ProgressBarProps> = ({
    value,
    width = "100%",
    height = "8px",
    backgroundColor = "#F5F5F5",
    progressColor = "#007F73",
    borderRadius = "4px",
    label,
    description,
    showTooltip = false,
    showSteps = true,
    onChange,
    max = 5,
    min = 0,
    step = 1,
    className,
    readOnly = false,
    scale
}) => {
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (onChange) {
            const newValue = Number(e.target.value);
            // 가장 가까운 정수로 조정
            const adjustedValue = Math.round(newValue);
            onChange(adjustedValue);
        }
    };

    return (
        <Container className={className}>
            {label && (
                <LabelWrapper>
                    <Label>{label}</Label>
                    {showTooltip && <TooltipIcon src="/icon/question-mark-icon.png" alt="도움말" />}
                </LabelWrapper>
            )}
            <ProgressBarWrapper>
                <ProgressBarContainer
                    $width={width}
                    $height={height}
                    $backgroundColor={backgroundColor}
                    $borderRadius={borderRadius}
                >
                    <Progress
                        $value={(value / max) * 100}
                        $progressColor={progressColor}
                        $borderRadius={borderRadius}
                    />
                    {showSteps && (
                        <StepsContainer>
                            <StepDivider />
                            <StepDivider />
                            <StepDivider />
                            <StepDivider />
                            <StepDivider />
                        </StepsContainer>
                    )}
                    {!readOnly && (
                        <>
                            <Thumb $value={value} $min={min} $max={max} />
                            <RangeInput
                                type="range"
                                min={min}
                                max={max}
                                step={step}
                                value={value}
                                onChange={handleChange}
                            />
                        </>
                    )}
                </ProgressBarContainer>
                {scale && scale.length >= 2 && (
                    <ScaleContainer>
                        <ScaleItem>{scale[0]}</ScaleItem>
                        <ScaleItem>{scale[scale.length - 1]}</ScaleItem>
                    </ScaleContainer>
                )}
            </ProgressBarWrapper>
            {description && <Description>{description}</Description>}
        </Container>
    );
};

export default ProgressBar;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const LabelWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 2px;
`;

const Label = styled.div`
    font-size: 14px;
    font-weight: 500;
    color: #545454;

    @media screen and (min-width: 640px) {
        font-size: 16px;
    }
`;

const TooltipIcon = styled.img`
    width: 12px;
    height: 12px;
    object-fit: contain;

    @media screen and (min-width: 640px) {
        width: 16px;
        height: 16px;
    }
`;

const Description = styled.p`
    font-size: 12px;
    color: #545454;
    font-weight: 500;
    text-align: center;
    margin-top: 2px;

    @media screen and (min-width: 640px) {
        font-size: 14px;
    }
`;

const ProgressBarWrapper = styled.div`
    width: 100%;
    padding: 0 1px;
`;

const ProgressBarContainer = styled.div<{
    $width: string;
    $height: string;
    $backgroundColor: string;
    $borderRadius: string;
}>`
    position: relative;
    width: ${(props) => props.$width};
    height: ${(props) => props.$height};
    background: ${(props) => props.$backgroundColor};
    border-radius: ${(props) => props.$borderRadius};
    overflow: visible;
`;

const Progress = styled.div<{
    $value: number;
    $progressColor: string;
    $borderRadius: string;
}>`
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: ${(props) => props.$value}%;
    background: ${(props) => props.$progressColor};
    border-radius: ${(props) => props.$borderRadius};
    transition: width 0.2s ease-in-out;
`;

const Thumb = styled.div<{
    $value: number;
    $min: number;
    $max: number;
}>`
    position: absolute;
    top: 50%;
    left: ${(props) => ((props.$value - props.$min) / (props.$max - props.$min)) * 100}%;
    transform: translate(-50%, -50%);
    width: 20px;
    height: 20px;
    background: #ffffff;
    border: 2px solid #007f73;
    border-radius: 50%;
    pointer-events: none;
    z-index: 2;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);

    @media screen and (min-width: 640px) {
        width: 24px;
        height: 24px;
    }
`;

const RangeInput = styled.input`
    position: absolute;
    top: 50%;
    left: 0;
    transform: translateY(-50%);
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
    z-index: 3;

    &::-webkit-slider-thumb {
        appearance: none;
        width: 20px;
        height: 20px;
        background: transparent;
        border: none;
        border-radius: 50%;
        cursor: pointer;

        @media screen and (min-width: 640px) {
            width: 24px;
            height: 24px;
        }
    }

    &::-moz-range-thumb {
        appearance: none;
        width: 20px;
        height: 20px;
        background: transparent;
        border: none;
        border-radius: 50%;
        cursor: pointer;

        @media screen and (min-width: 640px) {
            width: 24px;
            height: 24px;
        }
    }
`;

const StepsContainer = styled.div`
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    pointer-events: none;
    display: grid;
    grid-template-columns: repeat(5, 1fr);
`;

const StepDivider = styled.div`
    width: 100%;
    height: 100%;
    border-right: 2px solid #ffffff;
    opacity: 0.8;
    z-index: 1;

    &:last-child {
        border-right: none;
    }
`;

const ScaleContainer = styled.div`
    display: flex;
    justify-content: space-between;
    width: 100%;
    margin-top: 8px;
`;

const ScaleItem = styled.span`
    font-size: 12px;
    color: #545454;
    white-space: nowrap;

    @media screen and (min-width: 640px) {
        font-size: 14px;
    }
`;
